import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import classnames from "classnames";
import {Translation as T, useTranslation} from "@awe-web/shared/lib";
import {Button} from "../../components/form.js";
import {Row, Col} from "../../components/grid.js";
import {B2B_SelectStationAndTime, B2C_SelectStationAndTime} from "../../routing/routes.js";
import {SelectedStation, SelectedTime} from "./station.js";
import MinimalSelectedStationAndTime from "./minimal_selected_station_and_time";
import {
  getCoStation,
  getCiStation,
  getFormattedCoDateTime,
  getFormattedCiDateTime
} from "../store.js";

import "./selected_station_and_time.scss";

const CHANGE_VALUES = "FSD0000112";
const CHECK_OUT_STATION = "FSD0000107";
const CHECK_OUT_DATE_TIME = "FSD0000108";
const CHECK_IN_STATION = "FSD0000109";
const CHECK_IN_DATE_TIME = "FSD0000110";

export default function SelectedStationAndTime({ className, reservationPath, withEditButton, editButtonLocation }) {
  
  const {translate} = useTranslation();
  
  const stateCOStation = useSelector((state) => getCoStation(state));
  const coStation = useMemo(() => stateCOStation ? stateCOStation.toJS() : null, [stateCOStation]);

  const stateCIStation = useSelector((state) => getCiStation(state));
  const ciStation = useMemo(() => stateCIStation ? stateCIStation.toJS() : null, [stateCIStation]);

  const formattedCoDateTime = useSelector((state) => getFormattedCoDateTime(state));
  const formattedCiDateTime = useSelector((state) => getFormattedCiDateTime(state));

  const combinedClassName = classnames("selected-station-and-time", className);

  const editLocation = useMemo(() => {
    return editButtonLocation
      ? editButtonLocation
      : reservationPath === "B2B" ? B2B_SelectStationAndTime.path()
                                  : B2C_SelectStationAndTime.path();
  }, [editButtonLocation, reservationPath]);

  const renderEditButton = useCallback(() => {
    if (!withEditButton) {
      return null;
    }
    
    return (
      <Col desktop={{cols: 3, offset: 1}}>
        <Button
          className="selected-station-and-time__back-button"
          secondary
          back
          to={editLocation}
        >
          <T code={CHANGE_VALUES} />
        </Button>
      </Col>
    );
  }, [editLocation, withEditButton]);

  return (
    <div className={combinedClassName}>
      <MinimalSelectedStationAndTime
        reservationPath={reservationPath}
        coStationShortDescr={coStation?.shortdescr}
        formattedCoDateTime={formattedCoDateTime}
        formattedCiDateTime={formattedCiDateTime}
        withEditButton={withEditButton}
      />

      <div className="selected-station-and-time__stations-and-times">
        <Row alignCenter>
          <Col desktop={{cols: 2}}>
            <SelectedStation
              className="selected-station-and-time__selected-station--co"
              label={translate(CHECK_OUT_STATION)}
              shortdescr={coStation?.shortdescr}
            />
          </Col>
          <Col desktop={{cols: 2}}>
            <SelectedTime
              className="selected-station-and-time__selected-time--co"
              label={translate(CHECK_OUT_DATE_TIME)}
              formattedDateTime={formattedCoDateTime}
            />
          </Col>
          <Col desktop={{cols: 2}}>
            <SelectedStation
              className="selected-station-and-time__selected-station--ci"
              label={translate(CHECK_IN_STATION)}
              shortdescr={ciStation?.shortdescr}
            />
          </Col>
          <Col desktop={{cols: 2}}>
            <SelectedTime
              className="selected-station-and-time__selected-time--ci"
              label={translate(CHECK_IN_DATE_TIME)}
              formattedDateTime={formattedCiDateTime}
            />
          </Col>
          {renderEditButton()}
        </Row>
      </div>
    </div>
  );
}

SelectedStationAndTime.propTypes = {
  className: PropTypes.string,
  reservationPath: PropTypes.oneOf(["B2B", "B2C"]).isRequired,
  withEditButton: PropTypes.bool,
  editButtonLocation: Button.propTypes.to,
};
